/**
 * Single shared math-detection core for client and server. Framework-free ESM, no imports.
 *
 * Canonical home for finding CONTENT-5 (the `$...$` math-vs-currency detection logic was
 * duplicated VERBATIM three ways):
 *   - server/utils/mathDelimiterNormalizer.js (isLikelyMath, findDollarMathExpressions,
 *     BARE_LATEX_COMMANDS)
 *   - client/src/components/MathRenderer.jsx  (isLikelyMath, findDollarMathExpressions, patterns)
 *   - client/src/utils/latexRepair.js          (BARE_LATEX_COMMANDS, hasBareLatexCommand)
 *
 * Imported by the server via a relative path and by the client via the `@shared` Vite alias
 * (same mechanism as shared/validation/email.js from XCUT-4).
 *
 * BEHAVIOR CONTRACT: the server normalizer converts a `$...$` span only when THIS detector
 * treats it as math (zero-visual-change guarantee), and the client renderer renders exactly
 * the same spans. Any heuristic change here moves both sides together by construction.
 * The parity cases are pinned by server/tests/utils/mathDelimiterNormalizer.test.js and
 * client/src/tests/components/MathRenderer.test.jsx.
 */

// Pattern to detect common English words that indicate non-math content
export const ENGLISH_WORD_PATTERN =
  /\b(and|the|for|with|from|that|this|have|are|was|were|been|being|will|would|could|should|about|after|before|between|through|during|under|over|into|onto|upon|within|without|against|along|among|around|behind|below|beneath|beside|beyond|inside|outside|toward|towards|until|is|it|or|an|as|at|be|by|do|he|if|in|no|of|on|so|to|up|we|children?|tickets?|adult|child|sells?|cost|price|each|total|per|item|product|movie|theater|cinema|show|film|book|store|shop|buy|sell|pay|paid|dollars?|revenue|formula)\b/i

// Pattern to detect math-like characters and functions
export const MATH_PATTERN =
  /[+\-*/^=<>≤≥°′″±∞≠≈∝∠π()[\]{}\\]|[a-zA-Z][²³⁴⁵⁶⁷⁸⁹⁰]|\d+[a-zA-Z]|[a-zA-Z]\d|frac|sqrt|sum|int|lim|sin|cos|tan|log|ln/

// Pattern to detect numeric values (plain or comma-formatted, with optional decimals)
// Matches: 10, 1510, 1,510, 4,130, 1,000,000, 3.14, 1,234.56
export const CURRENCY_PATTERN = /^\d{1,3}(,\d{3})*(\.\d+)?$|^\d+(\.\d+)?$/

/**
 * Bare LaTeX relation/operator command words that authors sometimes write inside
 * `$...$` WITHOUT the leading backslash (e.g. `$t approx 5$`). KaTeX renders the bare
 * word as italic variable letters ("approx"), so the backslash must be restored
 * (`$t \approx 5$` → "t ≈ 5").
 *
 * This list is used both to DETECT a segment as math and to REPAIR it before KaTeX, so
 * it is deliberately limited to tokens that are unambiguously LaTeX and never plausible
 * English prose. Common words (`times`, `div`, `pm`, `mp`, `sim`) are intentionally
 * excluded: promoting them to math would mis-parse currency prose such as
 * "$5 times bigger than $10" (which must stay literal). Short tokens (`le`/`ge`/`ne`)
 * are excluded for the same reason.
 */
export const BARE_LATEX_COMMANDS = [
  'approx',
  'neq',
  'leq',
  'geq',
  'infty',
  'propto',
  'equiv',
  'cdots',
  'ldots',
  'cdot'
]

const BARE_LATEX_TEST_RE = new RegExp(`\\b(?:${BARE_LATEX_COMMANDS.join('|')})\\b`)

/**
 * True when `text` contains a bare (un-backslashed) LaTeX command word. Used by the
 * math detector so a segment like "t approx 5" is still recognized as math.
 * @param {unknown} text
 * @returns {boolean}
 */
export function hasBareLatexCommand(text) {
  if (!text || typeof text !== 'string') return false
  return BARE_LATEX_TEST_RE.test(text)
}

/**
 * Check if content between $...$ looks like math rather than currency or English text.
 * Returns true if it looks like math, false otherwise.
 * @param {string} content - The content between $...$ delimiters
 * @param {string|null} [_fullContext] - Optional: the full text being processed (unused, kept
 *   for call-site compatibility)
 * @returns {boolean}
 */
export function isLikelyMath(content, _fullContext = null) {
  if (!content) return false

  const trimmed = content.trim()

  // If it's just digits and decimal points, it's math when inside paired $...$.
  // Currency amounts are never written as $40$ — that's LaTeX notation.
  // Unpaired currency like "$40 worth" is already filtered out by the pairing algorithm.
  if (CURRENCY_PATTERN.test(trimmed)) {
    return true
  }

  // If it contains common English words, it's likely not math.
  // This catches "$12 and child tickets for $" type false matches.
  if (ENGLISH_WORD_PATTERN.test(content)) {
    return false
  }

  // If it contains math-like characters (operators, exponents, fractions, etc.) it's likely math.
  if (MATH_PATTERN.test(content)) {
    return true
  }

  // Bare LaTeX command words (e.g. "t approx 5" where the backslash was stripped upstream)
  // are math even though they contain no operator characters.
  if (hasBareLatexCommand(content)) {
    return true
  }

  // If content is very short and is just a single letter, it's likely a math variable.
  // But multi-word short phrases are likely English (e.g., "price per item").
  if (content.length < 50 && /[a-zA-Z]/.test(content)) {
    // Check if it's a single letter/variable (common in math like $x$, $n$, $p$)
    if (/^[a-zA-Z]$/.test(trimmed)) {
      return true
    }
    // For longer content, only return true if no spaces (likely a formula)
    if (!content.includes(' ')) {
      return true
    }
  }

  return false
}

/**
 * Find all valid $...$ LaTeX expressions in text. Returns [{ start, end, content }] where
 * text.slice(start, end) === "$...$".
 *
 * Handles the tricky case where currency like $12 appears before real math like $x + y$.
 *
 * Two symmetric currency guards keep a `$` that is really a currency sigil from being treated
 * as a delimiter: a `$` immediately preceded by a digit ("130$") is never an OPENING delimiter,
 * and a `$` immediately followed by a digit ("$90") is never a CLOSING delimiter. Together these
 * leave currency arithmetic such as "$6/lb gives $90" and "$90 + $135" literal.
 * @param {string} text
 * @returns {Array<{ start: number, end: number, content: string }>}
 */
export function findDollarMathExpressions(text) {
  const results = []
  const dollarPositions = []

  // Find all $ positions, skipping escaped \$ (LaTeX dollar-sign literal)
  for (let i = 0; i < text.length; i++) {
    if (text[i] === '$' && (i === 0 || text[i - 1] !== '\\')) {
      dollarPositions.push(i)
    }
  }

  // Try to pair $ signs, starting from the end (rightmost pairs first).
  // This helps avoid false matches like "$15 and... $x$" being parsed as one expression.
  const used = new Set()

  // Process pairs from right to left to find shortest valid matches
  for (let j = dollarPositions.length - 1; j >= 1; j--) {
    if (used.has(j)) continue

    const endPos = dollarPositions[j]

    // A $ immediately followed by a digit is a currency sigil that begins an amount
    // ("$90"), never a closing delimiter. Mirror of the opening-$ guard below that
    // rejects a $ preceded by a digit ("130$"). This keeps currency arithmetic like
    // "$6/lb gives $90" and "$90 + $135" literal instead of pairing it into math.
    if (endPos + 1 < text.length && /\d/.test(text[endPos + 1])) continue

    // Look for the closest opening $ that forms a valid math expression
    for (let i = j - 1; i >= 0; i--) {
      if (used.has(i)) continue

      const startPos = dollarPositions[i]

      // Skip $ immediately preceded by a digit — likely a postfix dollar sign ("130$"), not an
      // opening delimiter. In LaTeX, opening $ is preceded by whitespace/punctuation.
      if (startPos > 0 && /\d/.test(text[startPos - 1])) break

      const content = text.slice(startPos + 1, endPos)

      // Check if this looks like valid math (pass full text for context)
      if (isLikelyMath(content, text)) {
        results.unshift({ start: startPos, end: endPos + 1, content })
        used.add(i)
        used.add(j)
        break // Found a valid pair for this closing $
      }
    }
  }

  return results
}

// Regions where a `$` is NOT a math delimiter, or a `\$` is a legitimate literal dollar, and so must
// be excluded before judging whether the remaining dollars are mangled delimiters:
//   - \(...\) / \[...\]: normalized LaTeX math. Inside math mode `\$` correctly renders a dollar sign
//     (e.g. `\(\$40(5) + \$90\)` currency arithmetic), so those `\$` are valid, not corruption.
//   - fenced / inline code and <svg> / <math>: verbatim regions whose `$` carry no math meaning.
const MANGLED_SCAN_STRIP_REGIONS = [
  /```[\s\S]*?```/g, // fenced code block
  /`[^`\n]*`/g, // inline code
  /<svg[\s\S]*?<\/svg>/gi, // inline SVG
  /<math[\s\S]*?<\/math>/gi, // MathML
  /\\\([\s\S]*?\\\)/g, // inline LaTeX \(...\)
  /\\\[[\s\S]*?\\\]/g // display LaTeX \[...\]
]

// A math span mangled open-with-`\$`, closed-with-unescaped-`$`, on a single line:
//   `\$0.82$`, `\$82\%$`, `\$14.666...$`, and each broken piece of
//   `\$ \$ \$ \$ $21.5 - 20.25 = 1.25$ $`.
// `[^$\n]*` stops at the first `$` (escaped or not) and stays on one line so it can't bridge a
// legitimate `\$950` literal on one line to unrelated `$x$` math on another. The `(?<!\\)` lookbehind
// requires the CLOSING `$` to be unescaped — a `\$ ... \$` pair (two literal dollars, e.g. a `\$5`–`\$10`
// range) is left alone.
const MANGLED_DELIM_RE = /\\\$[^$\n]*(?<!\\)\$/

// A run of two or more escaped dollars separated only by whitespace: `\$\$\$\$`, `\$ \$ \$ \$`. This
// is the producer's mangling of `$$...$$` display math (and stray delimiter garbage) — `\$\$` renders
// as a literal `$$`, which never occurs in real prose. A single `\$` followed by digits (`\$5,000`)
// is NOT a run, so legitimate over-escaped currency is left alone.
const MANGLED_RUN_RE = /(?:\\\$\s*){2,}/

/**
 * Detect a specific corruption class: math spans written with `\$` (an escaped/literal dollar) used
 * as the OPENING delimiter instead of `$`, e.g. `\$0.82$`, `\$82\%$`, or the mixed/mangled forms
 * `\$ \$ \$ $21.5 - 20.25 = 1.25$ $`. These come from an upstream producer that over-escaped dollars;
 * a `\$` is a literal dollar, not a delimiter, so the span is unbalanced and cannot render.
 *
 * We do NOT reuse findDollarMathExpressions to "remove valid pairs" here: it treats the backslash in
 * `\$` as a math character and will greedily swallow `$ equals \$14.666...$` as one "valid" span,
 * hiding the corruption. Instead, after blanking regions where `\$` is legitimate (see
 * MANGLED_SCAN_STRIP_REGIONS — chiefly `\(...\)`/`\[...\]`, where `\$` is a valid in-math dollar
 * such as `\(\$40(5)\)`), we look directly for a `\$ … $` broken span. This deliberately does NOT flag:
 *   - clean `\(...\)` math or balanced `$x^2$` (no `\$` outside math),
 *   - a lone over-escaped literal like `\$950` with no closing `$` on the line (accepted "intended dollar"),
 *   - ordinary currency prose such as `$90 + $135` (no `\$` at all).
 *
 * @param {unknown} text
 * @returns {boolean} true when the field has mangled dollar-delimited math needing quarantine/repair.
 */
export function detectMangledDollarDelimiters(text) {
  if (!text || typeof text !== 'string') return false
  // This corruption class always involves an escaped `\$`; bail fast otherwise.
  if (text.indexOf('\\$') === -1) return false

  // Blank out regions where a `\$` is a legitimate literal dollar (LaTeX math) or carries no math
  // meaning (code/SVG/MathML), so only prose `\$` remain to judge.
  let stripped = text
  for (const re of MANGLED_SCAN_STRIP_REGIONS) {
    stripped = stripped.replace(re, ' ')
  }
  if (stripped.indexOf('\\$') === -1) return false // every `\$` was legitimate

  return MANGLED_DELIM_RE.test(stripped) || MANGLED_RUN_RE.test(stripped)
}
